import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from "@/components/ui/responsive-dialog";

type ButtonProps = React.ComponentProps<typeof Button>;

type ActionButtonConfig = {
  children: ReactNode;                 // можно передать иконку+текст или только текст
  onClick?: () => void;                // если не передан для cancel — закроет диалог
  variant?: ButtonProps["variant"];
  size?: ButtonProps["size"];
  className?: string;
  disabled?: boolean;
};

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;

  title: ReactNode;
  description?: ReactNode;

  isLoading?: boolean;

  cancelButton: ActionButtonConfig;
  confirmButton: ActionButtonConfig;

  contentClassName?: string;
  actionsClassName?: string;
}

export const ConfirmDialog = ({
  open,
  onOpenChange,
  title,
  description,
  isLoading = false,
  cancelButton,
  confirmButton,
  contentClassName,
  actionsClassName,
}: ConfirmDialogProps) => {
  const handleCancel = () => {
    cancelButton.onClick?.();
    if (!cancelButton.onClick) onOpenChange(false);
  };

  return (
    <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
      <ResponsiveDialogContent
        showCloseButton={false}
        className={[
          "rounded-md max-w-134 gap-6 border border-neutral-200/7 bg-neutral-200/2 p-2 backdrop-blur-[42px]",
          contentClassName,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <ResponsiveDialogHeader className="gap-2 pt-2">
          <ResponsiveDialogTitle className="fs-300 font-semibold text-neutral-100 text-center">
            {title}
          </ResponsiveDialogTitle>

          {description ? (
            <ResponsiveDialogDescription className="fs-200 text-neutral-200/50 text-center">
              {description}
            </ResponsiveDialogDescription>
          ) : null}
        </ResponsiveDialogHeader>

        <div
          className={[
            "grid grid-cols-2 gap-2",
            actionsClassName,
          ]
            .filter(Boolean)
            .join(" ")}
        >
          <Button
            variant={cancelButton.variant ?? "secondary"}
            size={cancelButton.size ?? "md"}
            className={["w-full", cancelButton.className].filter(Boolean).join(" ")}
            onClick={handleCancel}
            disabled={(cancelButton.disabled ?? false) || isLoading}
          >
            {cancelButton.children}
          </Button>

          <Button
            variant={confirmButton.variant ?? "primary"}
            size={confirmButton.size ?? "md"}
            className={["w-full", confirmButton.className].filter(Boolean).join(" ")}
            onClick={confirmButton.onClick}
            disabled={(confirmButton.disabled ?? false) || isLoading}
          >
            {confirmButton.children}
          </Button>
        </div>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
};